import { useTranslation } from "react-i18next";
import { buildLoginUrl } from "@/api/auth/oidcConfig";

type Role = "operator" | "end-user" | "admin";

interface SocialAuthButtonsProps {
  selectedRole: Role;
  oauthLoading: boolean;
  setOauthLoading: (v: boolean) => void;
  disabled?: boolean;
  isDark: boolean;
}



export default function SocialAuthButtons({
  selectedRole,
  oauthLoading,
  setOauthLoading,
  disabled = false,
  isDark,
}: SocialAuthButtonsProps) {
  const { t } = useTranslation();

  const go = (provider: string) => {
    setOauthLoading(true);
    let connection = provider;
    if (provider === "google") connection = "google-oauth2";

    // Provider-portable social login via our BFF (Authorization Code + PKCE).
    window.location.href = buildLoginUrl({ connection, role: selectedRole });
  };

  const isBusy = disabled || oauthLoading;

  const iconBtnBase: React.CSSProperties = {
    border: isDark ? "1px solid #4a4c4e" : "1px solid #d1d5db",
    backgroundColor: "transparent",
  };

  return (
    <div className="flex flex-col gap-3">
      {/* Google - full width */}
      <button
        type="button"
        onClick={() => go("google")}
        disabled={isBusy}
        className="w-full flex items-center justify-center gap-3 px-6 py-3 rounded-xl transition disabled:opacity-50"
        style={{
          border: isDark ? "1px solid #4a4c4e" : "1px solid #d1d5db",
          backgroundColor: "transparent",
          color: isDark ? "#e5e7eb" : "#374151",
        }}
        onMouseEnter={(e) =>
        (e.currentTarget.style.backgroundColor = isDark
          ? "rgba(255,255,255,0.04)"
          : "#f9fafb")
        }
        onMouseLeave={(e) =>
          (e.currentTarget.style.backgroundColor = "transparent")
        }
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
        </svg>
        <span className="font-medium">
          {t("login.googlebutton", { defaultValue: "Continue with Google" })}
        </span>
      </button>

      {/* Facebook + Microsoft + Yahoo - icon row */}
      <div className="flex gap-3">
        {/* Facebook - active */}
        <button
          type="button"
          onClick={() => go("facebook")}
          disabled={isBusy}
          title={t("login.facebookbutton", { defaultValue: "Continue with Facebook" })}
          aria-label={t("login.facebookbutton", { defaultValue: "Continue with Facebook" })}
          className="flex-1 flex items-center justify-center px-6 py-3 rounded-xl transition disabled:opacity-50"
          style={iconBtnBase}
          onMouseEnter={(e) =>
          (e.currentTarget.style.backgroundColor = isDark
            ? "rgba(255,255,255,0.04)"
            : "#f9fafb")
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.backgroundColor = "transparent")
          }
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#1877F2" d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.69.24 2.69.24v2.97h-1.52c-1.49 0-1.96.93-1.96 1.89v2.25h3.33l-.53 3.49h-2.8V24C19.61 23.1 24 18.1 24 12.07z" />
          </svg>
        </button>

        {/* Microsoft - soon */}
        <div className="relative flex-1">
          <button
            type="button"
            disabled
            title={t("login.microsoftbutton", { defaultValue: "Continue with Microsoft" })}
            aria-label={t("login.microsoftsoon", { defaultValue: "Continue with Microsoft (coming soon)" })}
            className="w-full flex items-center justify-center px-6 py-3 rounded-xl cursor-not-allowed opacity-50"
            style={iconBtnBase}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#F25022" d="M1 1h10.2v10.2H1z" />
              <path fill="#7FBA00" d="M12.8 1H23v10.2H12.8z" />
              <path fill="#00A4EF" d="M1 12.8h10.2V23H1z" />
              <path fill="#FFB900" d="M12.8 12.8H23V23H12.8z" />
            </svg>
          </button>
          <span
            className="absolute -top-2 -right-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full leading-none"
            style={{ backgroundColor: "#6b7280", color: "#ffffff" }}
          >
            {t("login.soon", { defaultValue: "soon" })}
          </span>
        </div>

        {/* Yahoo - soon */}
        <div className="relative flex-1">
          <button
            type="button"
            disabled
            title={t("login.yahoobutton", { defaultValue: "Continue with Yahoo" })}
            aria-label={t("login.yahoosoon", { defaultValue: "Continue with Yahoo (coming soon)" })}
            className="w-full flex items-center justify-center px-6 py-3 rounded-xl cursor-not-allowed opacity-50"
            style={iconBtnBase}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#6001D2" d="M3.15 4.5h3.96l2.31 5.91L11.76 4.5h3.85l-5.79 13.5H5.94l1.59-3.69L3.15 4.5zm12.06 5.4a2.31 2.31 0 110 4.62 2.31 2.31 0 010-4.62zm.84-5.4h4.2l-3.9 9h-4.17l3.87-9z" />
            </svg>
          </button>
          <span
            className="absolute -top-2 -right-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full leading-none"
            style={{ backgroundColor: "#6b7280", color: "#ffffff" }}
          >
            {t("login.soon", { defaultValue: "soon" })}
          </span>
        </div>
      </div>
    </div>
  );
}
